import { useState, useEffect } from "react";
import {
  Mail, Webhook, BarChart3, Download, Loader2, Plus, Trash2, Edit,
  CheckCircle2, XCircle, Search, Activity, FileText, Users, BookOpen,
  GraduationCap, Shield, ClipboardCheck, AlertTriangle, ExternalLink,
  Code, ChevronRight, Building2, TrendingUp, MoreHorizontal, Eye
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { cn } from "../../lib/utils";

const TABS = [
  { id: "emails", label: "Email Templates", icon: Mail },
  { id: "webhooks", label: "Webhooks", icon: Webhook },
  { id: "reports", label: "Cross-Tenant Report", icon: BarChart3 },
  { id: "export", label: "Data Export", icon: Download },
];

const WEBHOOK_EVENTS = [
  "enrollment.created", "enrollment.completed", "course.published",
  "user.created", "user.deactivated", "assessment.completed",
  "compliance.expired", "session.scheduled", "session.completed",
];

const EXPORT_ENTITIES = [
  { key: "users", label: "Users", icon: Users, description: "All user accounts" },
  { key: "courses", label: "Courses", icon: BookOpen, description: "Course catalog" },
  { key: "enrollments", label: "Enrollments", icon: GraduationCap, description: "All enrollment records" },
  { key: "assessments", label: "Assessments", icon: ClipboardCheck, description: "Assessment data" },
  { key: "compliance", label: "Compliance", icon: Shield, description: "Compliance templates" },
  { key: "sessions", label: "ILT Sessions", icon: Activity, description: "Session records" },
];

const AdvancedToolsPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("emails");
  const [loading, setLoading] = useState(true);

  // Email Templates
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [editEmail, setEditEmail] = useState(null);
  const [emailForm, setEmailForm] = useState({ subject: "", body_html: "", is_active: true });

  // Webhooks
  const [webhooks, setWebhooks] = useState([]);
  const [showWebhookDialog, setShowWebhookDialog] = useState(false);
  const [webhookForm, setWebhookForm] = useState({ name: "", url: "", events: [], tenant_id: "" });
  const [tenants, setTenants] = useState([]);

  // Cross-Tenant Report
  const [report, setReport] = useState([]);
  const [reportSearch, setReportSearch] = useState("");

  // Data Export
  const [exporting, setExporting] = useState("");
  const [exportTenantId, setExportTenantId] = useState("");

  const [saving, setSaving] = useState(false);

  const fetchEmails = async () => {
    try { const r = await api.get("/admin/email-templates"); setEmailTemplates(r.data || []); }
    catch {} finally { setLoading(false); }
  };

  const fetchWebhooks = async () => {
    try { const r = await api.get("/admin/webhooks"); setWebhooks(r.data || []); } catch {}
  };

  const fetchReport = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/cross-tenant-report"); setReport(r.data || []); }
    catch {} finally { setLoading(false); }
  };

  const fetchTenants = async () => {
    try { const r = await api.get("/tenants"); setTenants(r.data || []); } catch {}
  };

  useEffect(() => { fetchEmails(); fetchTenants(); }, []);
  useEffect(() => {
    if (activeTab === "webhooks") fetchWebhooks();
    if (activeTab === "reports") fetchReport();
  }, [activeTab]);

  const handleSaveEmail = async () => {
    if (!editEmail) return;
    setSaving(true);
    try {
      await api.put(`/admin/email-templates/${editEmail.template_key}`, emailForm);
      toast.success("Template updated");
      setEditEmail(null);
      fetchEmails();
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  const handleCreateWebhook = async () => {
    if (!webhookForm.name || !webhookForm.url) { toast.error("Name and URL required"); return; }
    setSaving(true);
    try {
      await api.post("/admin/webhooks", webhookForm);
      toast.success("Webhook created");
      setShowWebhookDialog(false);
      setWebhookForm({ name: "", url: "", events: [], tenant_id: "" });
      fetchWebhooks();
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  const handleDeleteWebhook = async (id) => {
    if (!window.confirm("Delete this webhook?")) return;
    try { await api.delete(`/admin/webhooks/${id}`); toast.success("Deleted"); fetchWebhooks(); }
    catch { toast.error("Failed"); }
  };

  const handleExport = async (entityType) => {
    setExporting(entityType);
    try {
      const params = exportTenantId ? { tenant_id: exportTenantId } : {};
      const r = await api.get(`/admin/export/${entityType}`, { params });
      const blob = new Blob([JSON.stringify(r.data.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${entityType}_export.json`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${r.data.count} ${entityType} records`);
    } catch { toast.error("Export failed"); }
    finally { setExporting(""); }
  };

  const filteredReport = report.filter(r =>
    !reportSearch || r.tenant_name?.toLowerCase().includes(reportSearch.toLowerCase())
  );

  const EMAIL_CATEGORIES = { Enrollment: "bg-blue-50 text-blue-700", Completion: "bg-green-50 text-green-700", Compliance: "bg-amber-50 text-amber-700", ILT: "bg-violet-50 text-violet-700", Assessment: "bg-red-50 text-red-700", Onboarding: "bg-teal-50 text-teal-700", Account: "bg-gray-50 text-gray-700" };

  return (
    <AdminLayout title="Advanced Tools">
      <div className="space-y-5 max-w-7xl" data-testid="advanced-tools-page">
        <div>
          <h1 className="text-xl font-bold" data-testid="tools-title">Advanced Tools</h1>
          <p className="text-sm text-muted-foreground">Email templates, webhooks, cross-tenant reporting, and data export</p>
        </div>

        <div className="flex items-center gap-1 border-b overflow-x-auto">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={cn("px-3 py-2 text-xs font-medium border-b-2 -mb-px whitespace-nowrap transition-colors flex items-center gap-1.5", activeTab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground")}
              data-testid={`tools-tab-${t.id}`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>

        {/* ═══ EMAIL TEMPLATES ═══ */}
        {activeTab === "emails" && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Customize system email templates. Variables like {"{{course_name}}"} are replaced automatically.</p>
            <div className="space-y-2" data-testid="email-list">
              {emailTemplates.map(et => (
                <Card key={et.template_key} className="border shadow-none">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4 min-w-0 flex-1">
                      <Mail className={cn("w-5 h-5 shrink-0", et.is_active ? "text-primary" : "text-muted-foreground/30")} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-0.5">
                          <p className="text-sm font-medium">{et.template_key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</p>
                          <Badge className={cn("text-[9px] px-1.5 py-0 border", EMAIL_CATEGORIES[et.category] || "bg-gray-50 text-gray-700")}>{et.category}</Badge>
                          {et.is_customized && <Badge className="text-[9px] px-1.5 py-0 bg-blue-50 text-blue-700 border-blue-200">Customized</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground truncate">{et.subject}</p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1 shrink-0" onClick={() => { setEditEmail(et); setEmailForm({ subject: et.subject, body_html: et.body_html || "", is_active: et.is_active !== false }); }}
                      data-testid={`edit-email-${et.template_key}`}>
                      <Edit className="w-3 h-3" /> Customize
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Dialog open={!!editEmail} onOpenChange={v => !v && setEditEmail(null)}>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle className="text-base">Edit: {editEmail?.template_key?.replace(/_/g, " ")}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label className="text-xs">Subject</Label><Input value={emailForm.subject} onChange={e => setEmailForm({ ...emailForm, subject: e.target.value })} className="h-8 text-sm" /></div>
                  <div><Label className="text-xs">Body HTML</Label><Textarea value={emailForm.body_html} onChange={e => setEmailForm({ ...emailForm, body_html: e.target.value })} rows={6} className="text-sm font-mono" placeholder="<h1>Hello {{learner_name}}</h1>..." /></div>
                  <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={emailForm.is_active} onChange={e => setEmailForm({ ...emailForm, is_active: e.target.checked })} className="rounded" /> Active</label>
                </div>
                <DialogFooter>
                  <Button variant="outline" size="sm" className="text-xs" onClick={() => setEditEmail(null)}>Cancel</Button>
                  <Button size="sm" className="text-xs gap-1" onClick={handleSaveEmail} disabled={saving}>{saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Save</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* ═══ WEBHOOKS ═══ */}
        {activeTab === "webhooks" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">Configure webhook endpoints for LMS events</p>
              <Button size="sm" className="text-xs h-7 gap-1" onClick={() => setShowWebhookDialog(true)} data-testid="create-webhook-btn">
                <Plus className="w-3 h-3" /> New Webhook
              </Button>
            </div>

            {webhooks.length === 0 ? (
              <Card className="border shadow-none"><CardContent className="py-12 text-center">
                <Webhook className="w-10 h-10 mx-auto text-muted-foreground/20 mb-3" />
                <p className="text-sm text-muted-foreground">No webhooks configured</p>
              </CardContent></Card>
            ) : (
              <div className="space-y-2" data-testid="webhooks-list">
                {webhooks.map(hook => (
                  <Card key={hook.id} className="border shadow-none">
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-start gap-3 min-w-0 flex-1">
                        <Webhook className={cn("w-5 h-5 shrink-0 mt-0.5", hook.is_active ? "text-green-600" : "text-muted-foreground/30")} />
                        <div className="min-w-0">
                          <p className="text-sm font-medium">{hook.name}</p>
                          <p className="text-[10px] text-muted-foreground truncate font-mono">{hook.url}</p>
                          <div className="flex items-center gap-1 mt-1 flex-wrap">
                            {(hook.events || []).slice(0, 4).map(e => <Badge key={e} variant="outline" className="text-[8px] px-1 py-0">{e}</Badge>)}
                            {(hook.events || []).length > 4 && <span className="text-[9px] text-muted-foreground">+{hook.events.length - 4}</span>}
                          </div>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:bg-red-50" onClick={() => handleDeleteWebhook(hook.id)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            <Dialog open={showWebhookDialog} onOpenChange={setShowWebhookDialog}>
              <DialogContent className="max-w-md">
                <DialogHeader><DialogTitle className="text-base">New Webhook</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label className="text-xs">Name</Label><Input value={webhookForm.name} onChange={e => setWebhookForm({ ...webhookForm, name: e.target.value })} className="h-8 text-sm" placeholder="My Webhook" /></div>
                  <div><Label className="text-xs">URL</Label><Input value={webhookForm.url} onChange={e => setWebhookForm({ ...webhookForm, url: e.target.value })} className="h-8 text-sm font-mono" placeholder="https://example.com/webhook" /></div>
                  <div><Label className="text-xs">Tenant (optional — blank = all)</Label>
                    <Select value={webhookForm.tenant_id} onValueChange={v => setWebhookForm({ ...webhookForm, tenant_id: v })}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="All tenants" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value=" ">All Tenants</SelectItem>
                        {tenants.map(t => <SelectItem key={t.id} value={t.id}>{t.name || t.company_name || t.id}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs mb-2 block">Events</Label>
                    <div className="grid grid-cols-1 gap-1">
                      {WEBHOOK_EVENTS.map(ev => (
                        <label key={ev} className="flex items-center gap-2 text-xs p-1.5 rounded border hover:bg-muted/30 cursor-pointer">
                          <input type="checkbox" checked={webhookForm.events.includes(ev)} onChange={e => {
                            const events = e.target.checked ? [...webhookForm.events, ev] : webhookForm.events.filter(x => x !== ev);
                            setWebhookForm({ ...webhookForm, events });
                          }} className="rounded" /> {ev}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" size="sm" className="text-xs" onClick={() => setShowWebhookDialog(false)}>Cancel</Button>
                  <Button size="sm" className="text-xs gap-1" onClick={handleCreateWebhook} disabled={saving} data-testid="save-webhook-btn">{saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Create</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* ═══ CROSS-TENANT REPORT ═══ */}
        {activeTab === "reports" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">Compare performance metrics across all tenants</p>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input placeholder="Search tenants..." value={reportSearch} onChange={e => setReportSearch(e.target.value)} className="pl-8 h-8 text-xs w-48" data-testid="report-search" />
              </div>
            </div>

            {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
              <Card className="border shadow-none overflow-hidden" data-testid="report-table">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead><tr className="border-b bg-muted/30">
                      <th className="p-3 text-left text-[10px] font-semibold uppercase text-muted-foreground">Tenant</th>
                      <th className="p-3 text-center text-[10px] font-semibold uppercase text-muted-foreground">Users</th>
                      <th className="p-3 text-center text-[10px] font-semibold uppercase text-muted-foreground">Courses</th>
                      <th className="p-3 text-center text-[10px] font-semibold uppercase text-muted-foreground">Enrollments</th>
                      <th className="p-3 text-center text-[10px] font-semibold uppercase text-muted-foreground">Completions</th>
                      <th className="p-3 text-center text-[10px] font-semibold uppercase text-muted-foreground">Rate</th>
                      <th className="p-3 text-center text-[10px] font-semibold uppercase text-muted-foreground">Compliance</th>
                      <th className="p-3 text-center text-[10px] font-semibold uppercase text-muted-foreground">Status</th>
                    </tr></thead>
                    <tbody>
                      {filteredReport.length === 0 ? (
                        <tr><td colSpan={8} className="p-8 text-center text-sm text-muted-foreground">No data</td></tr>
                      ) : filteredReport.map(r => (
                        <tr key={r.tenant_id} className="border-b hover:bg-muted/20">
                          <td className="p-3"><p className="text-sm font-medium">{r.tenant_name}</p></td>
                          <td className="p-3 text-center text-sm">{r.users}</td>
                          <td className="p-3 text-center text-sm">{r.courses}</td>
                          <td className="p-3 text-center text-sm font-medium">{r.enrollments}</td>
                          <td className="p-3 text-center text-sm">{r.completions}</td>
                          <td className="p-3 text-center">
                            <Badge className={cn("text-[9px] px-1.5 py-0 border", r.completion_rate >= 80 ? "bg-green-50 text-green-700 border-green-200" : r.completion_rate >= 50 ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-red-50 text-red-700 border-red-200")}>
                              {r.completion_rate}%
                            </Badge>
                          </td>
                          <td className="p-3 text-center text-sm">{r.compliance_items}</td>
                          <td className="p-3 text-center"><Badge className="text-[9px] bg-green-50 text-green-700 border-green-200">{r.status}</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </div>
        )}

        {/* ═══ DATA EXPORT ═══ */}
        {activeTab === "export" && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <p className="text-sm text-muted-foreground flex-1">Export data as JSON files. Filter by tenant or export all.</p>
              <Select value={exportTenantId} onValueChange={setExportTenantId}>
                <SelectTrigger className="h-8 text-xs w-48" data-testid="export-tenant-select"><SelectValue placeholder="All tenants" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value=" ">All Tenants</SelectItem>
                  {tenants.map(t => <SelectItem key={t.id} value={t.id}>{t.name || t.company_name || t.id}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="export-grid">
              {EXPORT_ENTITIES.map(entity => (
                <Card key={entity.key} className="border shadow-none hover:border-primary/30 transition-colors">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <entity.icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{entity.label}</p>
                        <p className="text-[10px] text-muted-foreground">{entity.description}</p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={() => handleExport(entity.key)} disabled={exporting === entity.key}
                      data-testid={`export-${entity.key}-btn`}>
                      {exporting === entity.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} Export
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdvancedToolsPage;
